import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Snackbar,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import React, { Fragment, useEffect, useState, forwardRef } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import MuiAlert from "@mui/material/Alert";
import { hasValue } from "../util/string";

export default function ConfirmDeductionPaymentDialog(props) {
  const { infoToConfirm } = props;

  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const Alert = forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={10} ref={ref} variant="filled" {...props} />;
  });

  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const validationSchema = yup.object({
    confirmationNumber: yup
      .string("Enter the number of the order you want to confirm")
      .required("The confirmation number is required")
      .max(30, "Max length 30"),
  });

  const handleSuccess = () => {
    setFeedback({
      open: true,
      type: "success",
      message: "The deduction payment has been confirmed successfully.",
      onClose: () => {
        queryClient.invalidateQueries({ queryKey: ["pad-dedudctions"] });
        setFeedback({ open: false });
        handleClose();
      },
    });
  };

  const queryClient = useQueryClient();

  const confirmOrderMutation = useMutation(
    (confirmationInfo) => {
      return HttpService.getAxiosClient().post(
        window.API_URL +
          `/pad/deductions/` +
          confirmationInfo.bundleId +
          "/confirm",
        {
          confirmationNumber: confirmationInfo.confirmationNumber,
        }
      );
    },
    {
      onSuccess: () => {
        handleSuccess();

        //handleClose();
      },
      onError: (err) => {
        const msg = err?.response?.data ? err.response.data : err.message;
        setFeedback({
          open: true,
          type: "error",
          message: msg,
        });
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      confirmationNumber: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      confirmOrderMutation.mutate({ ...values, bundleId: infoToConfirm.id });
    },
  });

  const handleClose = (event, reason) => {
    if (reason === "backdropClick") return; //avoid closing the modal by clicking the backdrop
    props.setOpen(false);
  };

  useEffect(() => {
    if (props.open === true) {
      formik.resetForm();
    }
  }, [props.open]);

  return (
    <Fragment>
      <Dialog
        open={props.open}
        onClose={props.handleClose}
        disableEscapeKeyDown={true}
      >
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={formik.handleSubmit}
        >
          <DialogTitle>{props.dialogTitle}</DialogTitle>
          <DialogContent>
            <DialogContentText>{props.dialogContentText}</DialogContentText>
            <br />
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  fullWidth
                  id="confirmationNumber"
                  label="Confirmation Number"
                  value={formik.values.confirmationNumber}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.confirmationNumber &&
                    Boolean(formik.errors.confirmationNumber)
                  }
                  helperText={
                    formik.touched.confirmationNumber &&
                    formik.errors.confirmationNumber
                  }
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color={"error"}>
              Cancel
            </Button>
            <Button
              disabled={!hasValue(formik.values.confirmationNumber)}
              type="submit"
            >
              {props.action}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={feedback.onClose ? feedback.onClose : handleFeedbackClose}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Fragment>
  );
}
